import { useState } from "react";

import type { ICompanyHeadquarterFilterDto } from "app/pages/sales-intelligence/modules/leads-search-filters";
import { toggleSelection } from "app/pages/sales-intelligence/modules/leads-search-filters/utils/toggleSelection";
import type { InclusionOption } from "app/pages/sales-intelligence/types/commonMaps";
import { inclusionOptionMap } from "app/pages/sales-intelligence/types/commonMaps";
import type { ICountryObject } from "app/services/CountryService";
import { US_COUNTRY_ID, WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import type { IRegionObject } from "app/services/RegionService";

import type FindCompanyHeadquarterFilter from "../../../../models/CompanyHeadquarterFilter/CompanyHeadquarterFilter";

interface IUseCompanyHeadquarterFilterProps {
    filter: FindCompanyHeadquarterFilter;
    value: ICompanyHeadquarterFilterDto | undefined;
    onFilterUpdate: (value: ICompanyHeadquarterFilterDto) => void;
}

const useCompanyHeadquarterFilter: ({ filter, value, onFilterUpdate }: IUseCompanyHeadquarterFilterProps) => {
    inclusionValue: InclusionOption;
    regions: readonly IRegionObject[];
    countries: readonly ICountryObject[];
    isSingleCountrySelected: boolean;
    isUSSelected: undefined | boolean;
    states: readonly ICountryObject[];
    handleInclusionSelection: (type: InclusionOption) => void;
    handleStateSelection: (country: { id: number } | null) => void;
    handleLocationChange: (codes: readonly number[]) => void;
    handleZipCodeBulkAdd: (codes: string[]) => void;
    handleZipCodeAdd: (code: string) => void;
} = ({ filter, value, onFilterUpdate }: IUseCompanyHeadquarterFilterProps) => {
    const [inclusionValue, setInclusionValue] = useState<InclusionOption>(
        value?.inclusion ?? inclusionOptionMap.includeOnly,
    );

    const { regions, countries, states } = filter.options;

    const updateFilter = (partial: Partial<ICompanyHeadquarterFilterDto>) => {
        onFilterUpdate({ ...value, ...partial, regions: [] });
    };

    const handleInclusionSelection = (type: InclusionOption) => {
        setInclusionValue(type);
        if (value?.codes?.length || value?.regions?.length) {
            updateFilter({ inclusion: type });
        }
    };

    const handleCountrySelection = (item: number, keepStates: boolean) => {
        const currentCodes = value?.codes?.filter((c) => c < WORLDWIDE_COUNTRY_ID || keepStates) ?? [];
        const updatedCodes = toggleSelection(currentCodes, item);

        updateFilter({
            codes: updatedCodes,
            zip: [],
            inclusion: inclusionValue,
        });
    };

    const handleLocationChange = (codes: readonly number[]) => {
        updateFilter({
            codes: codes.filter((c) => c < WORLDWIDE_COUNTRY_ID),
            zip: [],
            inclusion: inclusionValue,
        });
    };

    const handleStateSelection = (country: { id: number } | null) => {
        if (country) {
            handleCountrySelection(country.id, true);
        }
    };

    const handleZipCodeBulkAdd = (codes: string[]) => {
        const uniqueCodes = Array.from(new Set([...(value?.zip ?? []), ...codes]));
        updateFilter({ zip: uniqueCodes, inclusion: inclusionValue });
    };

    const handleZipCodeAdd = (code: string) => handleZipCodeBulkAdd([code]);

    const isSingleCountrySelected =
        !value?.regions?.length &&
        value?.codes?.filter((code) => countries.some((country) => country.id === code))?.length === 1;

    const isUSSelected = isSingleCountrySelected && value?.codes?.includes(US_COUNTRY_ID);

    return {
        inclusionValue,
        regions,
        countries,
        isSingleCountrySelected,
        isUSSelected,
        states,
        handleInclusionSelection,
        handleStateSelection,
        handleLocationChange,
        handleZipCodeBulkAdd,
        handleZipCodeAdd,
    };
};

export default useCompanyHeadquarterFilter;
